import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { services } from '../app';
import { starName } from '../data/catalog';
import { Vec3, enuToAltAz, refractEnu } from '../lib/math';
import { Selection, huntReticle, panel, selection, viewMode } from '../state';
import { whereInSky } from './format';
import { Icon } from './icons';

// Sky Hunt: find a handful of things in tonight's sky. Point the phone (or drag the view) until the
// target sits in the circle for a moment. Silent: success is shown, and felt as a short vibration.

interface Target {
  name: string;
  hint: string;
  sel: Selection;
  enu: () => Vec3;
  radius: number; // degrees: how close counts as found
}

function pickTargets(): Target[] {
  const s = services.value!;
  const e = s.engine;
  const up = (v: Vec3) => enuToAltAz(refractEnu(v)).alt > 12;
  const out: Target[] = [];
  // Where a body is now; the bodies are recomputed each frame, so keep the last answer if one is missing.
  const follow = (id: string, start: Vec3) => {
    let last = start;
    return () => {
      const b = e.bodies.find((x) => x.id === id);
      if (b) last = e.frame.toEnu(b.dir);
      return last;
    };
  };
  const moon = e.bodies.find((b) => b.id === 'Moon');
  if (moon && up(e.frame.toEnu(moon.dir))) out.push({ name: 'the Moon', hint: 'The easiest one to start with.', sel: { kind: 'body', id: 'Moon' }, enu: follow('Moon', e.frame.toEnu(moon.dir)), radius: 4 });
  for (const p of e.bodies.filter((b) => ['Venus', 'Jupiter', 'Mars', 'Saturn', 'Mercury'].includes(b.id)).sort((a, b) => a.mag - b.mag)) {
    if (out.length >= 2 || !up(e.frame.toEnu(p.dir)) || p.mag > 1.5) continue;
    out.push({ name: p.id, hint: 'A planet: it shines steadily instead of twinkling.', sel: { kind: 'body', id: p.id }, enu: follow(p.id, e.frame.toEnu(p.dir)), radius: 3.5 });
  }
  const cat = e.stars;
  let stars = 0;
  for (const i of cat.named) {
    if (stars >= 2 || cat.mag[i] > 1.4) break;
    const v = e.frame.toEnu(e.starDir(i));
    if (!up(v)) continue;
    stars++;
    out.push({ name: starName(cat, i), hint: 'One of the brightest stars in the sky.', sel: { kind: 'star', index: i }, enu: () => e.frame.toEnu(e.starDir(i)), radius: 3 });
  }
  // The Pole Star from the northern hemisphere: the one star that never moves.
  const polaris = cat.named.find((i) => cat.info.get(i)?.name === 'Polaris');
  if (polaris !== undefined && e.frame.observer.latitude > 15) {
    out.push({ name: 'Polaris, the Pole Star', hint: 'Due north, as high as your latitude. Follow the two end stars of the Plough upwards.', sel: { kind: 'star', index: polaris }, enu: () => e.frame.toEnu(e.starDir(polaris)), radius: 3 });
  }
  // A well-known constellation high up.
  const cons = e.cons.constellations.map((c, k) => ({ c, k, v: e.frame.toEnu(e.consGeo.anchors[k]) })).filter((x) => x.c.rank === 1 && enuToAltAz(x.v).alt > 25);
  cons.sort((a, b) => b.v[2] - a.v[2]);
  if (cons[0]) {
    const { c, k } = cons[0];
    out.push({ name: `the constellation ${c.name}`, hint: `Look for its pattern of bright stars.`, sel: { kind: 'constellation', index: k }, enu: () => e.frame.toEnu(e.consGeo.anchors[k]), radius: 9 });
  }
  return out.slice(0, 5);
}

export function HuntPlayer() {
  const s = services.value!;
  const targets = useMemo(pickTargets, []);
  const [i, setI] = useState(0);
  const [found, setFound] = useState<boolean[]>(() => targets.map(() => false));
  const [finished, setFinished] = useState(false);
  const [, tick] = useState(0);
  const heldSince = useRef(0);
  const t = targets[i];

  useEffect(() => {
    huntReticle.value = true;
    return () => {
      huntReticle.value = false;
      selection.value = null;
    };
  }, []);
  useEffect(() => {
    heldSince.current = 0;
    if (t) selection.value = t.sel;
  }, [i]);
  // Watch the view: found once the target has been inside the circle for a second.
  useEffect(() => {
    const id = setInterval(() => {
      tick((x) => x + 1);
      if (!t || found[i]) return;
      const v = t.enu();
      const c = s.engine.camera.forward();
      const off = (Math.acos(Math.max(-1, Math.min(1, c[0] * v[0] + c[1] * v[1] + c[2] * v[2]))) * 180) / Math.PI;
      if (off < t.radius) {
        if (!heldSince.current) heldSince.current = Date.now();
        else if (Date.now() - heldSince.current > 900) {
          const f = [...found];
          f[i] = true;
          setFound(f);
          navigator.vibrate?.(60);
        }
      } else heldSince.current = 0;
    }, 150);
    return () => clearInterval(id);
  }, [i, found]);

  const close = () => (panel.value = null);
  if (!targets.length) {
    return (
      <div class="tour hunt">
        <div class="tour-top"><span class="tour-step">Sky Hunt</span><button class="icon-btn" aria-label="Close" onClick={close}><Icon name="close" size={18} /></button></div>
        <p>Nothing bright enough is up right now. Try again once it is dark.</p>
      </div>
    );
  }
  const n = found.filter(Boolean).length;
  const done = finished || n === targets.length;
  const where = t ? enuToAltAz(refractEnu(t.enu())) : null;
  return (
    <div class="tour hunt" role="dialog" aria-label="Sky Hunt">
      <div class="tour-top">
        <span class="tour-step tnum">Sky Hunt · {found.filter(Boolean).length} of {targets.length} found</span>
        <button class="icon-btn" aria-label="End the hunt" onClick={close}><Icon name="close" size={18} /></button>
      </div>
      {done ? (
        <>
          <h3>{n === targets.length ? 'You found them all' : `You found ${n} of ${targets.length}`}</h3>
          <p>{n === targets.length ? 'Well done. ' : ''}Tap anything in the sky to learn more about it, or try again another night: the sky changes with the seasons.</p>
          <div class="tour-controls"><button class="btn small" onClick={close}>Done</button></div>
        </>
      ) : (
        <>
          <h3>{found[i] ? `Found ${t.name}` : `Find ${t.name}`}</h3>
          <p aria-live="polite">
            {found[i]
              ? 'Nicely done.'
              : `${where ? `${whereInSky(where.alt, where.az)}. ` : ''}${t.hint} ${viewMode.value === 'ar' ? 'Point your phone until it sits in the circle.' : 'Drag the sky until it sits in the circle.'}`}
          </p>
          <div class="tour-controls">
            <button class="btn-ghost" disabled={i === 0} onClick={() => setI(i - 1)} aria-label="Previous"><Icon name="back" size={16} /></button>
            {i < targets.length - 1 ? (
              <button class={found[i] ? 'btn small' : 'btn-ghost small'} onClick={() => setI(i + 1)}>{found[i] ? 'Next' : 'Skip'} <Icon name="forward" size={14} /></button>
            ) : (
              <button class="btn small" onClick={() => setFinished(true)}>Finish</button>
            )}
          </div>
        </>
      )}
      <div class="tour-dots">{targets.map((_, k) => <span key={k} class={`${k === i ? 'on' : ''} ${found[k] ? 'found' : ''}`} />)}</div>
    </div>
  );
}
